import React, { useEffect, useState } from "react";
import { db } from "../firebase"; // Adjust path to your Firebase configuration
import { collection, onSnapshot, doc, updateDoc } from "firebase/firestore";

function Customers() {
  const [customers, setCustomers] = useState([]); // State to hold customers data
  const [stats, setStats] = useState({
    avgRating: NaN, // Placeholder for average rating
    avgDeliveryTime: NaN, // Placeholder for average delivery time
  });
  const [loading, setLoading] = useState(true); // Loading state for customers data
  const [loadingStats, setLoadingStats] = useState(true); // Loading state for stats data

  useEffect(() => {
    // Real-time listener for customer data with error handling
    const unsubscribeCustomers = onSnapshot(
      collection(db, "customers"),
      (snapshot) => {
        const customersData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setCustomers(customersData);
        setLoading(false); // Data is loaded
      },
      (error) => {
        console.error("Error fetching customers: ", error);
        setLoading(false); // Handle loading state on error
      }
    );

    // Real-time listener for average rating and delivery time data with error handling
    const unsubscribeOrders = onSnapshot(
      collection(db, "orders"),
      (snapshot) => {
        const orders = snapshot.docs.map((doc) => doc.data());

        // Calculate average rating and delivery time
        const totalRating = orders.reduce((acc, order) => acc + (order.rating || 0), 0);
        const totalDeliveryTime = orders.reduce((acc, order) => acc + (order.deliveryTime || 0), 0);

        const avgRating = orders.length > 0 ? totalRating / orders.length : NaN;
        const avgDeliveryTime = orders.length > 0 ? totalDeliveryTime / orders.length : NaN;

        setStats((prevStats) => {
          if (prevStats.avgRating !== avgRating || prevStats.avgDeliveryTime !== avgDeliveryTime) {
            return {
              avgRating, // Update the average rating if it changes
              avgDeliveryTime, // Update the average delivery time if it changes
            };
          }
          return prevStats; // Prevent unnecessary re-renders if stats are the same
        });
        setLoadingStats(false); // Stats data is loaded
      },
      (error) => {
        console.error("Error fetching orders: ", error);
        setLoadingStats(false); // Handle loading state on error
      }
    );

    // Cleanup the listeners when the component unmounts
    return () => {
      unsubscribeCustomers();
      unsubscribeOrders();
    };
  }, []); // Empty dependency array means this runs once when the component mounts

  // Function to block or unblock a customer
  const handleBlockUnblock = async (customerId, isBlocked) => {
    const customerRef = doc(db, "customers", customerId); // Reference to the customer document
    try {
      await updateDoc(customerRef, { blocked: !isBlocked }); // Toggle the blocked status
      console.log("Customer status updated");
    } catch (error) {
      console.error("Error updating customer status: ", error);
    }
  };

  return (
    <div className="p-4">
      <h1 className="text-2xl font-bold mb-4">Customers</h1>

      {loading ? (
        <div className="text-gray-500 text-center mt-10">Loading customers...</div>
      ) : customers.length === 0 ? (
        <div className="text-gray-500 text-center mt-10">No customers yet</div>
      ) : (
        <table className="w-full border-collapse border border-gray-300">
          <thead>
            <tr className="bg-gray-100">
              <th className="p-2 border">#</th>
              <th className="p-2 border">Name</th>
              <th className="p-2 border">Email</th>
              <th className="p-2 border">Status</th>
              <th className="p-2 border">Action</th>
            </tr>
          </thead>
          <tbody>
            {/* Rendering customer data */}
            {customers.map((cust, index) => (
              <tr key={cust.id}>
                <td className="p-2 border">{index + 1}</td>
                <td className="p-2 border">{cust.name}</td>
                <td className="p-2 border">{cust.email}</td>
                <td className="p-2 border">{cust.blocked ? "Blocked" : "Active"}</td>
                <td className="p-2 border">
                  <button
                    className={`px-3 py-1 text-white rounded ${cust.blocked ? "bg-green-500" : "bg-red-500"}`}
                    onClick={() => handleBlockUnblock(cust.id, cust.blocked)}
                  >
                    {cust.blocked ? "Unblock" : "Block"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Displaying stats for average rating and delivery time */}
      <div className="mt-6">
        <h2 className="text-xl font-bold">Performance Stats</h2>
        <div className="mt-4">
          <div className="flex justify-between">
            <p>Average Rating:</p>
            <p>{loadingStats ? "Loading..." : isNaN(stats.avgRating) ? "N/A" : stats.avgRating.toFixed(2)}</p>
          </div>
          <div className="flex justify-between mt-2">
            <p>Average Delivery Time (mins):</p>
            <p>{loadingStats ? "Loading..." : isNaN(stats.avgDeliveryTime) ? "N/A" : stats.avgDeliveryTime.toFixed(2)}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Customers;
